import { alpha } from "@mui/material/styles";

// Shared visual language for the admin dashboards (/admin/usage and
// /admin/events). Centralised here so both pages stay consistent and the
// contrast tuning lives in one place.

// Accent palette used to colour stat cards. High-saturation hues that all clear
// WCAG-AA contrast against the near-black admin surface.
export const ADMIN_ACCENTS = [
  "#F2E600", // signal yellow (hybra brand)
  "#5AD1FF", // steel-blue / cyan
  "#7CD313", // lime (hybra success)
  "#FF9F0A", // amber (hybra warning)
  "#A78BFA", // violet
  "#36C5A8", // teal
  "#FF8FAB", // pink
  "#FF453A", // red (hybra error)
];

export const accentForIndex = (i) => ADMIN_ACCENTS[i % ADMIN_ACCENTS.length];

// Page shell for every admin section. AdminLayout renders a paddingless
// <Outlet/>, so each section owns its own gutter — but they previously each
// picked their own padding and, crucially, NONE constrained width, so tables
// and forms sprawled edge-to-edge on wide monitors. Centralise a single
// centred, max-width column with consistent responsive gutters so every tab
// reads as the same console.
export const adminPageSx = {
  maxWidth: 1160,
  mx: "auto",
  px: { xs: 2, sm: 3, md: 4 },
  py: { xs: 2, sm: 3 },
};

// Standard vertical gap below a section header and between stacked sections —
// so the rhythm doesn't drift (was a mix of mb:3 / mb:4 across pages).
export const ADMIN_SECTION_MB = 3;

// Monospace stack for ids / raw payloads. Was re-declared as an inline string
// literal in four dashboards; single source of truth here.
export const MONO =
  '"SFMono-Regular", "Menlo", "Consolas", "Liberation Mono", monospace';

// The 7d / 30d / 90d window toggle styling — was copy-pasted verbatim into
// three dashboards. One token keeps the selected-state accent consistent.
export const adminToggleGroupSx = (theme) => ({
  "& .MuiToggleButton-root": {
    borderColor: alpha("#ffffff", 0.12),
    color: theme.palette.text.secondary,
  },
  "& .Mui-selected": {
    color: `${theme.palette.primary.contrastText} !important`,
    backgroundColor: `${theme.palette.primary.main} !important`,
  },
});

// Tinted "chip" for a category/candidate tag. Every page hand-rolled this with
// drifting alphas (0.14 vs 0.16) and colour pairings; standardise on the accent
// as both tint and text (both clear AA on the dark surface) with a faint edge.
export const adminChipSx = (theme, accent = theme.palette.primary.light) => ({
  backgroundColor: alpha(accent, 0.16),
  color: accent,
  fontWeight: 600,
  border: `1px solid ${alpha(accent, 0.32)}`,
});

// A surface that reads clearly above background.default — a subtle top-lit
// gradient plus a visible border so the card has an edge even before its
// shadow. `accent`/`active` paint the selected/hover affordance.
export const adminSurfaceSx = (theme, { accent, active = false } = {}) => {
  const edge = active
    ? accent || theme.palette.primary.main
    : alpha("#ffffff", 0.08);
  return {
    borderRadius: 3,
    border: `1px solid ${edge}`,
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(180deg, ${alpha(
      "#ffffff",
      0.03
    )} 0%, ${alpha("#ffffff", 0)} 60%)`,
    boxShadow: active
      ? `0 0 0 1px ${alpha(accent || theme.palette.primary.main, 0.5)}, 0 8px 24px ${alpha(
          "#000",
          0.4
        )}`
      : theme.shadows[2],
    transition:
      "transform .16s ease, border-color .16s ease, box-shadow .16s ease",
  };
};

// Interactive (clickable) stat card — adds the hover lift on top of the surface.
export const adminClickableCardSx = (theme, opts = {}) => ({
  ...adminSurfaceSx(theme, opts),
  cursor: "pointer",
  height: "100%",
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: opts.accent || alpha("#ffffff", 0.22),
    boxShadow: `0 10px 28px ${alpha("#000", 0.5)}`,
  },
});

// Small tinted icon "chip" that gives each stat card a colour anchor.
export const adminIconBadgeSx = (accent) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: 2,
  backgroundColor: alpha(accent, 0.16),
  color: accent,
  flexShrink: 0,
});

// Wrapper around a table so it reads as a contained panel with a header band.
export const adminTableWrapSx = (theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha("#ffffff", 0.08)}`,
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
});

export const adminTableHeadCellSx = (theme) => ({
  backgroundColor: alpha("#ffffff", 0.05),
  color: theme.palette.text.secondary,
  fontWeight: 700,
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  borderBottom: `1px solid ${alpha("#ffffff", 0.1)}`,
  whiteSpace: "nowrap",
});

// Zebra striping + hover for table body rows.
export const adminTableRowSx = {
  "&:nth-of-type(odd)": { backgroundColor: alpha("#ffffff", 0.035) },
  "&:hover": { backgroundColor: alpha("#ffffff", 0.07) },
  "& td": { borderBottom: `1px solid ${alpha("#ffffff", 0.06)}` },
};
